/*
  Golden Lighting Wizard — Ceiling Height engine (shared, topic-specific)
  rulebase v2.3 · rules: AP-015, AP-013, CL-002, SC-005, PL-013
  drafted 2026-07-07

  WHAT THIS FILE IS: pure logic, no DOM. Defines the question graph (STEPS)
  and the recommendation function (recommend) for the ceiling-height topic.
  Any renderer can drive this — the standalone SEO-pillar embed
  (gl-wizard-runtime.js) today, and the eventual "Product Genius" catalog-wide
  orchestrator later. Neither renderer should ever hardcode this topic's
  copy/thresholds — they read it from here, so there is exactly one place
  to edit when the rulebase changes.

  HOW TO ADD THE NEXT TOPIC (chandelier sizing, vanity lighting, etc.):
  copy this file's shape - RULES object, STEPS array, recommend(state)
  function, register onto window.GLWizard.<topicId> - and any renderer that
  already knows how to walk GLWizard.ceilingHeight will work on the new
  topic for free.

  DEPLOY AS A THEME ASSET (not pasted inline) so both the standalone pillar
  embed and the future Product Genius page load the exact same file -
  nothing gets copy-pasted or rebuilt twice. Shopify admin -> Online Store ->
  Themes -> Edit code -> Assets -> Add a new asset -> upload this file as
  "gl-ceiling-engine.js".
*/
(function (global) {
  "use strict";
  var GLWizard = global.GLWizard = global.GLWizard || {};

  // ---- RULE CONTENT (mirrors lighting rulebase v2.json, compile_wizard rules) ----
  var RULES = {
    lowCeilingMaxFt: 8, // AP-015 threshold
    wideSemiFlushMinIn: 24, // AP-015
    clearanceFt: 7, // CL-002 (HARD)
    lampCounts: { // AP-013, extended by AP-015 exception
      small: "around 3 lamps (tables 48 in or less)",
      medium: "5 to 6 lamps (tables 54–72 in)",
      large: "a wide (24 in+) semi-flush with 6 or more lamps — AP-015's exception lets a wide semi-flush serve even a 6-seat-or-larger table, which normally needs a bigger hung fixture"
    },
    lowCeilingCopy: "At {ceilingFt} ft, a hanging pendant or chandelier has very little room to work with: code requires {clearanceFt} ft of clearance from the floor to the fixture's bottom in walking areas (CL-002), which barely leaves room for the fixture itself — let alone hanging it low enough over a table to look right. The fix isn't a smaller fixture, it's a different fixture type: a wide semi-flush mount ({wideIn} in or more across, with a generous lamp count) solves the ceiling problem by spreading out instead of hanging down. Mounted close to the ceiling, it never fights the clearance rule, and its width gives it real presence.",
    normalCeilingCopy: "At {ceilingFt} ft you have healthy clearance to work with — a hanging pendant or chandelier is a great option here. Fixture body height generally runs 2.5–3 inches per foot of ceiling height (SC-005), so a taller ceiling can carry a larger, taller fixture.",
    bedroomWarning: "One note for bedrooms specifically: skip open-bottom, bare-lamp flush or semi-flush fixtures — anyone lying down looks straight up into the light source (PL-013). Choose a diffused or enclosed-bottom design instead.",
    citeLow: "Rules cited: AP-015 (wide semi-flush for low ceilings), CL-002 (7-ft clearance, code), SC-005 (fixture scale).",
    citeNormal: "Rules cited: SC-005 (fixture scale), CL-002 (7-ft clearance, code).",
    citeBedroom: "Also cited: PL-013 (open-bottom fixtures excluded from bedrooms)."
  };

  function fmt(tpl, vals) {
    return tpl.replace(/\{(\w+)\}/g, function (_, k) { return vals[k]; });
  }

  // ---- QUESTION GRAPH ----
  // Generic shape any runtime can walk: { id, prompt, options:[{label,value,next}], helpLink? }
  // "result" is a reserved step id meaning "call recommend(state) and show it".
  var STEPS = {
    ceiling: {
      id: "ceiling",
      prompt: "What's your ceiling height?",
      field: "ceiling",
      options: [
        { label: "8 ft or lower (common in ranches, split-levels, older homes)", value: 8, next: "room" },
        { label: "9 ft", value: 9, next: "room" },
        { label: "10 ft or higher", value: 10, next: "room" }
      ],
      helpLink: {
        label: "Not sure — how do I check?",
        prompt: "Quick way to check",
        text: "Measure straight up from the floor to the ceiling in the room where the fixture will go. Most homes built before 1980 have 8-ft ceilings; homes from the 1990s on often have 9-ft or vaulted ceilings."
      }
    },
    room: {
      id: "room",
      prompt: "Where's this fixture going?",
      field: "room",
      options: [
        { label: "Dining room (over a table)", value: "dining", next: "seats" },
        { label: "Living room / great room", value: "living", next: "result" },
        { label: "Bedroom", value: "bedroom", next: "result" },
        { label: "Hallway or entryway", value: "hallway", next: "result" }
      ]
    },
    seats: {
      id: "seats",
      prompt: "How many seats at the table?",
      field: "seats",
      options: [
        { label: "4 or fewer", value: "small", next: "result" },
        { label: "5–6", value: "medium", next: "result" },
        { label: "7 or more", value: "large", next: "result" }
      ]
    }
  };

  // ---- RECOMMENDATION (pure function: state in, render-agnostic data out) ----
  function recommend(state) {
    var isLow = state.ceiling <= RULES.lowCeilingMaxFt;
    var paragraphs = [];

    paragraphs.push(fmt(isLow ? RULES.lowCeilingCopy : RULES.normalCeilingCopy, {
      ceilingFt: state.ceiling, clearanceFt: RULES.clearanceFt, wideIn: RULES.wideSemiFlushMinIn
    }));

    if (isLow && state.room === "dining" && state.seats) {
      paragraphs.push("For your table size: " + RULES.lampCounts[state.seats] + ".");
    }
    if (state.room === "bedroom") {
      paragraphs.push(RULES.bedroomWarning);
    }

    var citation = (isLow ? RULES.citeLow : RULES.citeNormal) + (state.room === "bedroom" ? " " + RULES.citeBedroom : "");

    return {
      title: isLow ? "Go wide, not down" : "You have room to hang",
      paragraphs: paragraphs,
      citation: citation,
      cta: {
        label: isLow ? "Shop wide semi-flush mounts" : "Shop chandeliers & pendants",
        href: "/collections/semi-flush" // Semi-Flush collection (live handle, 2026-07-10)
      },
      secondaryLink: {
        label: "Read the full guide",
        href: "/blogs/the-golden-edit/how-to-light-low-ceilings-why-wide-semi-flush-mounts-are-the-answer" // pillar 06 (publish the draft first)
      }
    };
  }

  GLWizard.ceilingHeight = {
    id: "ceiling-height",
    label: "Ceiling Height & Fixture Type",
    rules: ["AP-015", "AP-013", "CL-002", "SC-005", "PL-013"],
    entryStep: "ceiling",
    steps: STEPS,
    recommend: recommend
  };
})(window);
